﻿import React from 'react';
import {
  Layers,
  Play,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Trash2,
  Clock,
  Plus,
  Sparkles,
  Archive,
  Eye,
  Paintbrush,
} from 'lucide-react';
import { BatchPhotoItem } from '../types';

interface BatchQueueDrawerProps {
  queue: BatchPhotoItem[];
  activeId: string | null;
  onSelectPhoto: (id: string) => void;
  onRemovePhoto: (id: string, e: React.MouseEvent) => void;
  onProcessBatch: () => void;
  isProcessingBatch: boolean;
  onAddMoreFiles: (files: FileList | null) => void;
  onDownloadAllZip?: () => void;
  isZipping?: boolean;
}

export const BatchQueueDrawer: React.FC<BatchQueueDrawerProps> = ({
  queue,
  activeId,
  onSelectPhoto,
  onRemovePhoto,
  onProcessBatch,
  isProcessingBatch,
  onAddMoreFiles,
  onDownloadAllZip,
  isZipping = false,
}) => {
  const completedCount = queue.filter(
    (item) => item.status === 'completed' && item.versions.length > 0
  ).length;
  const reviewCount = queue.filter((item) => item.status === 'ready_for_review').length;
  const pendingCount = queue.filter((item) => item.status === 'idle').length;
  const errorCount = queue.filter((item) => item.status === 'error').length;
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const getStatusBadge = (status: BatchPhotoItem['status']) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
            <CheckCircle2 className="w-3 h-3" /> Restored
          </span>
        );
      case 'ready_for_review':
        return (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-teal-800 bg-teal-50 px-2 py-0.5 rounded-full border border-teal-200">
            <Eye className="w-3 h-3 text-teal-600" /> Confirm Settings
          </span>
        );
      case 'analyzing':
        return (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-teal-800 bg-teal-50 px-2 py-0.5 rounded-full border border-teal-200 animate-pulse">
            <RefreshCw className="w-3 h-3 animate-spin" /> Analyzing
          </span>
        );
      case 'restoring':
        return (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full border border-teal-200 animate-pulse">
            <Sparkles className="w-3 h-3 animate-spin" /> Restoring
          </span>
        );
      case 'error':
        return (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-700 bg-red-50 px-2 py-0.5 rounded-full border border-red-200">
            <AlertCircle className="w-3 h-3" /> Error
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-500 bg-stone-800 px-2 py-0.5 rounded-full border border-slate-300">
            <Clock className="w-3 h-3" /> Queued
          </span>
        );
    }
  };

  const executableCount = reviewCount + pendingCount + errorCount;

  return (
    <div className="kin-studio-panel overflow-hidden shadow-sm">
      {/* Queue Header & Global Actions */}
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center space-x-2.5">
          <div className="w-7 h-7 rounded-lg bg-teal-50 border border-teal-200 flex items-center justify-center text-teal-700">
            <Layers className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-xs sm:text-sm font-bold text-slate-900 flex items-center gap-2">
              Batch Restoration Queue
              <span className="px-2 py-0.2 rounded-full text-[10px] bg-teal-50 text-teal-800 border border-teal-200 font-mono">
                {queue.length} {queue.length === 1 ? 'photo' : 'photos'}
              </span>
            </h3>
            <p className="text-[11px] text-slate-600">
              {completedCount} completed • {reviewCount} awaiting confirmation • {pendingCount} queued {errorCount > 0 && `• ${errorCount} error`}
            </p>
          </div>
        </div>

        <div className="flex items-center flex-wrap gap-2">
          {/* Add more photos to batch */}
          <input
            type="file"
            ref={fileInputRef}
            multiple
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              onAddMoreFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center space-x-1 px-2.5 py-1.5 bg-white hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-medium border border-slate-300 transition-colors"
          >
            <Plus className="w-3.5 h-3.5 text-teal-600" />
            <span className="hidden sm:inline">Add to Queue</span>
          </button>

          {/* Bulk Download ZIP button */}
          {completedCount > 0 && onDownloadAllZip && (
            <button
              onClick={onDownloadAllZip}
              disabled={isZipping || isProcessingBatch}
              className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-200 text-white rounded-lg text-xs font-semibold border border-emerald-500 transition-colors shadow-sm"
              title="Download all currently restored photos as a single .ZIP file"
            >
              {isZipping ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 animate-spin text-emerald-100" />
                  <span>Packaging ZIP...</span>
                </>
              ) : (
                <>
                  <Archive className="w-3.5 h-3.5 text-emerald-100" />
                  <span>Download All ({completedCount}) [.ZIP]</span>
                </>
              )}
            </button>
          )}

          {/* Process entire batch */}
          {executableCount > 0 && (
            <button
              onClick={onProcessBatch}
              disabled={isProcessingBatch}
              className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-teal-600 hover:bg-teal-500 disabled:bg-slate-200 disabled:text-slate-500 text-white font-bold rounded-lg text-xs transition-colors shadow-sm"
            >
              {isProcessingBatch ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  <span>Processing Batch...</span>
                </>
              ) : (
                <>
                  <Play className="w-3.5 h-3.5 fill-current" />
                  <span>Restore All ({executableCount})</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Queue items horizontal carousel */}
      <div className="p-3 bg-slate-50 flex items-center gap-3 overflow-x-auto scrollbar-thin scrollbar-thumb-slate-300">
        {queue.map((item, index) => {
          const isActive = item.id === activeId;
          const displayImage =
            item.versions.length > 0
              ? item.versions[item.versions.length - 1].imageUrl
              : item.sourceImage.dataUrl;

          return (
            <div
              key={item.id}
              onClick={() => onSelectPhoto(item.id)}
              className={`relative shrink-0 w-44 rounded-xl border p-2 cursor-pointer transition-all ${
                isActive
                  ? 'bg-teal-50 border-teal-400 shadow-md ring-1 ring-teal-300'
                  : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              {/* Thumbnail preview */}
              <div className="relative w-full h-24 rounded-lg overflow-hidden bg-slate-100 flex items-center justify-center border border-slate-200">
                <img
                  src={displayImage}
                  alt={item.fileName}
                  className="w-full h-full object-cover select-none"
                />

                {/* Status overlay badge */}
                <div className="absolute top-1.5 left-1.5">
                  {getStatusBadge(item.status)}
                </div>

                {/* Remove button */}
                <button
                  onClick={(e) => onRemovePhoto(item.id, e)}
                  className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-stone-950/80 hover:bg-red-900 text-slate-500 hover:text-red-200 flex items-center justify-center transition-colors border border-slate-300"
                  title="Remove from queue (will not be included in ZIP)"
                >
                  <Trash2 className="w-3 h-3" />
                </button>

                {/* Mask badge if applied */}
                {item.damageMask && (
                  <div className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-mono bg-red-50 text-red-700 border border-red-200 flex items-center gap-0.5">
                    <Paintbrush className="w-2.5 h-2.5" /> Mask
                  </div>
                )}

                {item.rotation !== 0 && (
                  <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded text-[9px] font-mono bg-stone-950/80 text-teal-800 border border-stone-800">
                    {item.rotation}°
                  </div>
                )}
              </div>

              {/* Title & metadata */}
              <div className="mt-2 space-y-0.5">
                <div className="flex items-center justify-between text-xs font-semibold text-slate-800">
                  <span className="truncate" title={item.fileName}>
                    #{index + 1} {item.fileName}
                  </span>
                </div>
                <p className="text-[10px] text-slate-500 truncate">
                  {item.versions.length > 0
                    ? `${item.versions.length} version(s)`
                    : item.status === 'ready_for_review'
                    ? 'Awaiting user approval'
                    : 'Awaiting processing'}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

